import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useGetExclusiveDealsApi } from './use-get-exclusive-deals-api';

const endpoint = 'ums/v1/cms/exclusive-deals';
export const createExclusiveDealEndpointIdentifier = endpoint;

interface BodyType {
  productId: number;
  countryCode: string;
  cashbackPercent?: number;
  dealPrice?: number;
  productExpiryDate?: string | null;
  isNearExpiry?: boolean;
  // Defaults true server-side. false records the negotiated price as a deal
  // without publishing it to restaurants yet (the workbench inline-create flow).
  isActive?: boolean;
}

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  body: BodyType;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useCreateExclusiveDealApi(options?: Options): ReturnType {
  const { refetch: refetchDeals } = useGetExclusiveDealsApi({ enabled: false });
  const toast = useCustomToast();

  const mutationFn = async ({ body }: TVariables) => {
    try {
      const { data } = await http.post(endpoint, body);

      // Refetch deals list
      refetchDeals();

      toast({
        description: 'New exclusive deal has been created successfully',
        status: 'success',
      });

      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { mutationKey: endpoint, ...options });
}
